import type { UseFormReturn } from 'react-hook-form';

import type {
  CheckUserExistenceActionState,
  RegisterActionState,
} from '@/app/(auth)/actions';
import { toast } from '@/components/toast';
import type { RegisterFormData } from '@/stores/register/types';

export type SubscriptionType =
  | 'pro-monthly'
  | 'pro-annual'
  | 'premium-monthly'
  | 'premium-annual'
  | null;

export type RegisterStatusCallBackStatusType = Exclude<
  RegisterActionState['status'],
  'idle' | 'in_progress' | 'success'
>;

/**
 * Which register server action was dispatched by the submit handler.
 * Tracked in a ref so the completion effect knows which `isPending` flag to
 * watch and which action state to read the resulting status from.
 */
export type RegisterActionKind = 'credentials' | 'social';

/**
 * Outcome of resolving a register action completion. The Factory effect maps
 * each variant to its side effects (toast / success callback / button reset).
 *
 * - `none`    — no action dispatched yet, or it is still in flight.
 * - `error`   — action finished with a user-facing failure status; show the
 *               matching toast and release the submit button.
 * - `success` — account created; run the success callback (navigation/OAuth).
 * - `release` — action finished on an idle/in_progress (or unknown) status;
 *               just release the button so it isn't stuck.
 */
export type RegisterCompletion =
  | { type: 'none' }
  | { type: 'error'; status: RegisterStatusCallBackStatusType }
  | { type: 'success' }
  | { type: 'release' };

/**
 * Pure decision function for the register completion effect.
 *
 * `useActionState` only re-runs a status-based effect when the status
 * CHANGES, so a repeated identical failure would never re-fire the toast.
 * This function is driven
 * off the `isPending` transition plus an in-flight ref instead, so it returns
 * a non-`none` outcome every time the dispatched action completes —
 * regardless of whether the status changed between attempts.
 */
export const resolveRegisterCompletion = (args: {
  kind: RegisterActionKind | null;
  isPending: boolean;
  socialIsPending: boolean;
  status?: string;
}): RegisterCompletion => {
  const { kind, isPending, socialIsPending, status } = args;

  // Only react when the action we actually dispatched has finished.
  const completed =
    (kind === 'credentials' && !isPending) ||
    (kind === 'social' && !socialIsPending);

  if (!completed) {
    return { type: 'none' };
  }

  if (
    status === 'cloudwalk_unavailable' ||
    status === 'cloudwalk_validation_error' ||
    status === 'phone_verification_required' ||
    status === 'rate_limited' ||
    status === 'failed' ||
    status === 'invalid_data'
  ) {
    return { type: 'error', status };
  }

  if (status === 'success') {
    return { type: 'success' };
  }

  return { type: 'release' };
};

export interface RegisterPageFactory {
  registerForm: UseFormReturn<RegisterFormData>;
  handleSubmit: (data: RegisterFormData) => Promise<void>;
  redirect_type?: string | null;
  callbackUrl?: string | null;
  subscription_plan?: string | null;
  isSendRegisterForm: boolean;
  redirectAfterLogin: () => void;
}

export const getRegisterCheckErrorMessage = (
  status: 'failed' | 'invalid_data',
) => {
  if (status === 'failed') {
    return 'Erro ao verificar email, telefone e CPF. Tente novamente.';
  }

  return 'Dados inválidos. Verifique email, telefone e CPF.';
};

export const generateFormDataToSend = (
  data: RegisterFormData,
  socialAuthParams?: {
    provider: string;
    providerUserId: string;
    /**
     * Signed proof minted by the OAuth `signIn` callback (issue #5801).
     * `registerWithSocialAuth` rejects the request without a valid,
     * matching token — threading it through here is required for social
     * signups to succeed, not optional plumbing.
     */
    socialRegistrationToken?: string;
  },
): FormData => {
  const formData = new FormData();

  formData.append('name', data.name.trim().replace(/\s+/g, ' '));
  formData.append('email', data.email);

  if (!socialAuthParams && data.password) {
    formData.append('password', data.password);
  }

  formData.append('document', data.document);
  formData.append('dateOfBirth', data.dateOfBirth);
  formData.append('dateOfBirthTerms', data.dateOfBirthTerms.toString());
  formData.append('avatarImage', data.avatarImage);

  if (data.phoneNumber) {
    formData.append('phoneNumberDisplay', data.phoneNumber);
    formData.append(
      'phoneNumber',
      `55${data.phoneNumber.replace(/\D/g, '').trim()}`,
    );
  }

  if (socialAuthParams) {
    formData.append('provider', socialAuthParams.provider);
    formData.append('providerUserId', socialAuthParams.providerUserId);
    if (socialAuthParams.socialRegistrationToken) {
      formData.append(
        'socialRegistrationToken',
        socialAuthParams.socialRegistrationToken,
      );
    }
  }

  return formData;
};

export const registerFormSubmitResultDataError = (
  result: CheckUserExistenceActionState,
) => {
  const errorMap = {
    document: 'CPF já está em uso!',
    emailDocument: 'Email e CPF já estão cadastrados!',
    phoneDocument: 'Telefone e CPF já estão cadastrados!',
    all: 'Email, telefone e CPF já estão cadastrados!',
    both: 'Email e telefone já estão cadastrados!',
    email: 'Email já está cadastrado!',
    phone: 'Telefone já está cadastrado!',
  };

  const errorKey = result.byEmail
    ? result.byPhone
      ? result.byDocument
        ? 'all'
        : 'both'
      : result.byDocument
        ? 'emailDocument'
        : 'email'
    : result.byPhone
      ? result.byDocument
        ? 'phoneDocument'
        : 'phone'
      : result.byDocument
        ? 'document'
        : null;

  const message = errorKey ? errorMap[errorKey] : 'Conta já existe!';

  toast({ type: 'error', description: message });
};
